//? Import the necessary dependencies
import { useEffect, useState } from "react";

//? Importing icons
import { Button, Form, Spinner, Row, Col } from "react-bootstrap";

//* Import the useNavigate and useParams hooks from react-router-dom
import { useNavigate, useParams } from "react-router-dom";

//? Import moment to handle dates
import moment from "moment";

//? Import components 
import { ErrorMessages } from "../../../component/common/ErrorMessages";
import { Attention } from "../../../component/base/Attention";

//? Import the useApplications hook from the applications hook
import { useApplications } from "../../../hooks/applications/useApplications";

//* Import the CreateApplicationInput type from the applications hook
import { CreateApplicationInput } from "../../../hooks/applications/types/types";

interface ApplicationFormProps {
  editMode?: boolean;
}

export function ApplicationForm({ editMode }: ApplicationFormProps) {
  const navigate = useNavigate();
  const params = useParams();
  const token = localStorage.getItem("token") || "";

  const {
    getApplicationById,
    createApplication,
    updateApplication,
    isCreating,
    isUpdating,
  } = useApplications(token);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    applicationType: "online" as "online" | "classroom",
    initDate: moment().format("YYYY-MM-DD"),
    initTime: moment().format("HH:mm"),
    endDate: moment().format("YYYY-MM-DD"),
    endTime: moment().format("HH:mm"),
    correctionEndDate: moment().format("YYYY-MM-DD"),
    correctionEndTime: moment().format("HH:mm"),
    questionOrder: "register" as "register" | "random",
    alternativeOrder: "register" as "register" | "random",
  });

  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    description?: string;
  }>({});
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const clearErrors = () => {
    setErrorMessages([]);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (editMode && params.id) {
        setIsLoadingData(true);
        try {
          const dataFromBackend = await getApplicationById(params.id);
          if (dataFromBackend) {
            setFormData({
              name: dataFromBackend.name || "",
              description: dataFromBackend.description || "",
              applicationType: dataFromBackend.isOnline
                ? "online"
                : "classroom",
              initDate: moment(dataFromBackend.startDate).format("YYYY-MM-DD"),
              initTime: moment(dataFromBackend.startDate).format("HH:mm"),
              endDate: moment(dataFromBackend.endDate).format("YYYY-MM-DD"),
              endTime: moment(dataFromBackend.endDate).format("HH:mm"),
              correctionEndDate: moment(dataFromBackend.deadlineDate).format(
                "YYYY-MM-DD"
              ),
              correctionEndTime: moment(dataFromBackend.deadlineDate).format(
                "HH:mm"
              ),
              questionOrder: dataFromBackend.questionOrder
                ? "register"
                : "random",
              alternativeOrder: dataFromBackend.alternativeOrder
                ? "register"
                : "random",
            });
          }
        } catch (err) {
          console.error("Erro ao buscar dados:", err);
        } finally {
          setIsLoadingData(false);
        }
      }
    };
    fetchData();
  }, [editMode, params.id, getApplicationById]);

  const validateForm = (): boolean => {
    const newErrorMessages: string[] = [];

    const start = moment(
      `${formData.initDate} ${formData.initTime}`,
      "YYYY-MM-DD HH:mm"
    );
    const end = moment(
      `${formData.endDate} ${formData.endTime}`,
      "YYYY-MM-DD HH:mm"
    );
    const correctionEnd = moment(
      `${formData.correctionEndDate} ${formData.correctionEndTime}`,
      "YYYY-MM-DD HH:mm"
    );

    if (!start.isBefore(end)) {
      newErrorMessages.push(
        "A data/hora de início deve ser menor que a de encerramento."
      );
    }

    if (!end.isBefore(correctionEnd)) {
      newErrorMessages.push(
        "A data/hora de encerramento deve ser menor que a data/hora final para entrega das notas."
      );
    }

    setErrorMessages(newErrorMessages);
    return newErrorMessages.length === 0;
  };

  const handleNameChange = (value: string) => {
    setFormData((prev) => ({ ...prev, name: value }));
    if (value.trim().length < 3) {
      setFieldErrors((prev) => ({
        ...prev,
        name: "O nome deve ter pelo menos 3 caracteres.",
      }));
    } else {
      setFieldErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const handleDescriptionChange = (value: string) => {
    setFormData((prev) => ({ ...prev, description: value }));
    if (value.length > 200) {
      setFieldErrors((prev) => ({
        ...prev,
        description: "Descrição muito longa, máximo de 200 caracteres.",
      }));
    } else {
      setFieldErrors((prev) => ({ ...prev, description: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.name.trim().length < 3) {
      setFieldErrors((prev) => ({
        ...prev,
        name: "O nome deve ter pelo menos 3 caracteres.",
      }));
    }

    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    const payload: CreateApplicationInput = {
      name: formData.name,
      description: formData.description,
      is_online: formData.applicationType === "online",
      start_date: `${formData.initDate} ${formData.initTime}:00`,
      end_date: `${formData.endDate} ${formData.endTime}:00`,
      deadline_date: `${formData.correctionEndDate} ${formData.correctionEndTime}:00`,
      question_in_order: formData.questionOrder === "register",
      alternative_order: formData.alternativeOrder === "register",
    };

    try {
      if (editMode && params.id) {
        await updateApplication({ id: params.id, applicationData: payload });
      } else {
        await createApplication(payload);
      }
      navigate("/tests");
    } catch (err) {
      console.error("Erro ao salvar aplicação:", err);
    }
  };

  const endTimeMin =
    formData.initDate === formData.endDate ? formData.initTime : undefined;
  const correctionEndTimeMin =
    formData.endDate === formData.correctionEndDate
      ? formData.endTime
      : undefined;
  const isLoading = isCreating || isUpdating || isLoadingData;

  return (
    <div className="row">
      <div className="col-12">
        <div className="py-5 px-3">
          <div className="card-body" id="card"></div>
          <h4 className="mt-0 header-title">
            {editMode ? "Editar Aplicação" : "Cadastrar Aplicação"}
          </h4>
          <p>
            {editMode
              ? "Edite a aplicação do seu sistema"
              : "Cadastre as aplicações do seu sistema"}
          </p>
          <br />

          <ErrorMessages messages={errorMessages} clearErrors={clearErrors} />

          {isLoading && (
            <div className="d-flex justify-content-center align-items-center">
              <Spinner animation="border" variant="primary" role="status" />
            </div>
          )}

          {!isLoading && (
            <Form onSubmit={handleSubmit} data-testid="application-form">
              <Form.Group className="mb-3">
                <Form.Label>
                  Nome <Attention />
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: Prova 1 - Cálculo I"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  required
                  data-testid="input-name"
                />
                {fieldErrors.name && (
                  <small style={{ color: "red" }}>{fieldErrors.name}</small>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Descrição</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Ex: Prova de cálculo I..."
                  value={formData.description}
                  onChange={(e) => handleDescriptionChange(e.target.value)}
                  data-testid="input-description"
                />
                {fieldErrors.description && (
                  <small style={{ color: "red" }}>
                    {fieldErrors.description}
                  </small>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Tipo de aplicação <Attention />
                </Form.Label>
                <Form.Select
                  value={formData.applicationType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      applicationType: e.target.value as "online" | "classroom",
                    })
                  }
                  data-testid="select-application-type"
                >
                  <option value="online">Prova Online</option>
                  <option value="classroom">Prova Presencial</option>
                </Form.Select>
              </Form.Group>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Data de início <Attention />
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.initDate}
                      onChange={(e) =>
                        setFormData({ ...formData, initDate: e.target.value })
                      }
                      max={formData.endDate}
                      required
                      data-testid="input-init-date"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Horário de início <Attention />
                    </Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.initTime}
                      onChange={(e) =>
                        setFormData({ ...formData, initTime: e.target.value })
                      }
                      required
                      data-testid="input-init-time"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Data de encerramento <Attention />
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.endDate}
                      onChange={(e) =>
                        setFormData({ ...formData, endDate: e.target.value })
                      }
                      min={formData.initDate}
                      required
                      data-testid="input-end-date"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Horário de encerramento <Attention />
                    </Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.endTime}
                      min={endTimeMin}
                      onChange={(e) =>
                        setFormData({ ...formData, endTime: e.target.value })
                      }
                      required
                      data-testid="input-end-time"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col sm={6}>
                  <Form.Group>
                    <Form.Label>
                      Data final para entrega das notas <Attention />
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.correctionEndDate}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          correctionEndDate: e.target.value,
                        })
                      }
                      min={formData.endDate}
                      required
                      data-testid="input-correction-end-date"
                    />
                  </Form.Group>
                </Col>

                <Col sm={6}>
                  <Form.Group>
                    <Form.Label>
                      Horário final para entrega das notas <Attention />
                    </Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.correctionEndTime}
                      min={correctionEndTimeMin}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          correctionEndTime: e.target.value,
                        })
                      }
                      required
                      data-testid="input-correction-end-time"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>
                  Ordem das questões <Attention />
                </Form.Label>
                <div data-testid="question-order-options">
                  <Form.Check
                    inline
                    type="radio"
                    label="Questões em ordem de cadastro"
                    name="questionOrder"
                    checked={formData.questionOrder === "register"}
                    onChange={() =>
                      setFormData({ ...formData, questionOrder: "register" })
                    }
                    style={{ fontSize: "1.2rem", marginBottom: "0.5rem" }}
                    data-testid="radio-question-order-register"
                  />
                  <br />
                  <Form.Check
                    inline
                    type="radio"
                    label="Questões em ordem aleatória"
                    name="questionOrder"
                    checked={formData.questionOrder === "random"}
                    onChange={() =>
                      setFormData({ ...formData, questionOrder: "random" })
                    }
                    style={{ fontSize: "1.25rem" }}
                    data-testid="radio-question-order-random"
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Ordem das alternativas <Attention />
                </Form.Label>
                <div data-testid="alternative-order-options">
                  <Form.Check
                    inline
                    type="radio"
                    label="Alternativas na ordem de cadastro"
                    name="alternativeOrder"
                    checked={formData.alternativeOrder === "register"}
                    onChange={() =>
                      setFormData({ ...formData, alternativeOrder: "register" })
                    }
                    style={{ fontSize: "1.2rem", marginBottom: "0.7rem" }}
                    data-testid="radio-alternative-order-register"
                  />
                  <br />
                  <Form.Check
                    inline
                    type="radio"
                    label="Alternativas em ordem aleatória"
                    name="alternativeOrder"
                    checked={formData.alternativeOrder === "random"}
                    onChange={() =>
                      setFormData({ ...formData, alternativeOrder: "random" })
                    }
                    style={{ fontSize: "1.2rem", marginBottom: "0.7rem" }}
                    data-testid="radio-alternative-order-random"
                  />
                </div>
              </Form.Group>

              <div className="d-flex justify-content-between mt-3">
                <Button 
                  variant="secondary" 
                  onClick={() => navigate("/tests")}
                  data-testid="btn-back"
                >
                  Voltar
                </Button>
                <Button 
                  variant="success" 
                  type="submit"
                  data-testid="btn-submit"
                >
                  {editMode ? "Atualizar" : "Salvar"}
                </Button>
              </div>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
}
